import {
  ComposedChart,
  // Bar, // Revenue features disabled for MVP
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts'
import { format, parseISO } from 'date-fns'

export interface TimeSeriesDataPoint {
  date: string
  visitors: number
  revenue: number
  leads?: number
}

interface DualAxisChartProps {
  data: TimeSeriesDataPoint[]
  isLoading?: boolean
}

// Custom tooltip component
function CustomTooltip({ active, payload, label }: TooltipProps<number, string>) {
  if (!active || !payload || !payload.length) return null

  const data = payload[0]?.payload as TimeSeriesDataPoint
  const visitors = data?.visitors || 0
  // Revenue features disabled for MVP - uncomment when revenue tracking is enabled
  // const revenue = data?.revenue || 0
  // const revenuePerVisitor = visitors > 0 ? revenue / visitors : 0
  // const conversionRate = visitors > 0 ? ((revenue > 0 ? 1 : 0) / visitors) * 100 : 0

  return (
    <div className="chart-tooltip">
      <div className="font-semibold text-white mb-2">
        {label ? format(parseISO(label), 'MMM d, yyyy') : ''}
      </div>
      <div className="space-y-1 text-sm">
        <div className="flex items-center justify-between gap-4">
          <span className="text-gray-400">👥 Visitors</span>
          <span className="font-medium text-white">{visitors.toLocaleString()}</span>
        </div>
        {/* Revenue features disabled for MVP - uncomment when revenue tracking is enabled */}
        {/* <div className="flex items-center justify-between gap-4">
          <span className="text-gray-400">💰 Revenue</span>
          <span className="font-medium text-white">${revenue.toLocaleString()}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-gray-400">💵 Rev/Visitor</span>
          <span className="font-medium text-white">${revenuePerVisitor.toFixed(2)}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-gray-400">📈 Conv. Rate</span>
          <span className="font-medium text-white">{conversionRate.toFixed(2)}%</span>
        </div> */}
      </div>
    </div>
  )
}

// Format axis ticks
function formatVisitorAxis(value: number): string {
  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`
  return value.toString()
}

// Revenue features disabled for MVP
// function formatRevenueAxis(value: number): string {
//   if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`
//   return `$${value}`
// }

function formatDateAxis(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'MMM d')
  } catch {
    return dateStr
  }
}

export function DualAxisChart({ data, isLoading }: DualAxisChartProps) {
  if (isLoading) {
    return (
      <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-80 flex items-center justify-center bg-gray-50 rounded-lg">
        <div className="text-center">
          <span className="text-4xl mb-2 block">📊</span>
          <p className="text-gray-500">No data available for this period</p>
        </div>
      </div>
    )
  }

  // Calculate max values for axis scaling
  const maxVisitors = Math.max(...data.map((d) => d.visitors), 1)
  // Revenue features disabled for MVP
  // const maxRevenue = Math.max(...data.map((d) => d.revenue), 1)

  return (
    <div className="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="var(--color-chart-grid, #e5e7eb)"
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateAxis}
            tick={{ fontSize: 12, fill: 'var(--color-chart-axis, #9ca3af)' }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            yAxisId="visitors"
            orientation="left"
            tickFormatter={formatVisitorAxis}
            tick={{ fontSize: 12, fill: 'var(--color-chart-axis, #9ca3af)' }}
            axisLine={false}
            tickLine={false}
            domain={[0, Math.ceil(maxVisitors * 1.1)]}
            width={50}
          />
          {/* Revenue axis disabled for MVP - uncomment when revenue tracking is enabled */}
          {/* <YAxis
            yAxisId="revenue"
            orientation="right"
            tickFormatter={formatRevenueAxis}
            tick={{ fontSize: 12, fill: 'var(--color-chart-axis, #9ca3af)' }}
            axisLine={false}
            tickLine={false}
            domain={[0, Math.ceil(maxRevenue * 1.1)]}
            width={60}
          /> */}
          <Tooltip content={<CustomTooltip />} />
          {/* Revenue bar disabled for MVP - uncomment when revenue tracking is enabled */}
          {/* <Bar
            yAxisId="revenue"
            dataKey="revenue"
            fill="var(--color-chart-bar, #fed7aa)"
            radius={[4, 4, 0, 0]}
            barSize={24}
          /> */}
          <Line
            yAxisId="visitors"
            type="monotone"
            dataKey="visitors"
            stroke="var(--color-chart-line, #f97316)"
            strokeWidth={2}
            dot={false}
            activeDot={{
              r: 6,
              fill: 'var(--color-chart-line, #f97316)',
              stroke: '#ffffff',
              strokeWidth: 2,
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
